// File: client/src/components/analytics/CallCard.js
import React, { useMemo, useCallback } from 'react';
import { format } from 'date-fns';
import { get } from 'lodash';
import {
    getColorFromPercentage,
    evaluateExpression,
    calculateMaxValue,
    getQualityColor, getSmoothQualityColor
} from './color-util';
import { useModal } from './ModalContext';

// Memoized formatDate function to prevent redundant calculations
const formatDate = (dateString) => {
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            console.error('Invalid date:', dateString);
            return { time: '--:--', date: '---' };
        }
        return {
            time: format(date, 'h:mm a'),
            date: format(date, 'MMM d, yyyy'),
        };
    } catch (error) {
        console.error('Error formatting date:', error);
        return { time: '--:--', date: '---' };
    }
};

// Memoized Flag component
const Flag = React.memo(({ label, style }) => (
    <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 12px',
        borderRadius: '16px',
        fontSize: '13px',
        fontWeight: 500,
        ...style,
    }}>
        {label}
    </span>
));

// Memoized Tag component
const Tag = React.memo(({ label, value, style }) => {
    if (value == null) return null;
    return (
        <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 12px',
            borderRadius: '16px',
            fontSize: '13px',
            fontWeight: 500,
            backgroundColor: style?.backgroundColor || '#f3f4f6',
            color: style?.color || '#6b7280',
        }}>
            {`${label}: ${value}`}
        </span>
    );
});

// Memoized Column component
const Column = React.memo(({ label, value, type = 'text' }) => {
    const displayValue = type === 'score' ? `${Math.round(value || 0)}%` : value || '--';
    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            justifyContent: 'center',
            height: '100%',
        }}>
            <span style={{
                fontSize: '12px',
                color: '#6b7280',
                fontWeight: '400',
                lineHeight: '1.2',
            }}>
                {label}
            </span>
            <span style={{
                fontSize: '14px',
                color: '#111827',
                fontWeight: '600',
                lineHeight: '1.2',
            }}>
                {displayValue}
            </span>
        </div>
    );
});

// Memoized DateTimeColumn component
const DateTimeColumn = React.memo(({ date }) => {
    const formattedDate = useMemo(() => formatDate(date), [date]);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            minWidth: '120px',
            justifyContent: 'center',
            height: '100%',
        }}>
            <span style={{
                fontSize: '12px',
                color: '#6b7280',
                fontWeight: '400',
                lineHeight: '1.2',
            }}>
                {formattedDate.date}
            </span>
            <span style={{
                fontSize: '14px',
                color: '#111827',
                fontWeight: '600',
                lineHeight: '1.2',
            }}>
                {formattedDate.time}
            </span>
        </div>
    );
});

// Move outside component to prevent recreation on each render
const evaluateCondition = (condition, record) => {
    try {
        return Function('record', `return ${condition}`)(record);
    } catch (e) {
        console.error('Error evaluating condition: ' + condition, e);
        return false;
    }
};

// Memoized helper function to calculate field values
const calculateFieldValue = (item, call) => {
    const fieldName = item.field || item.id;
    let rawValue = get(call, fieldName);
    if (item.calculation) {
        if (item.calculation.type === 'average' && item.calculation.fields) {
            const values = item.calculation.fields.map(f => Number(get(call, f)) || 0);
            rawValue = values.reduce((a, b) => a + b, 0) / values.length;
        }
    }
    if (item.expression && item.expression.trim() !== '') {
        try {
            const fn = new Function('record', 'value', 'return ' + item.expression);
            return fn(call, rawValue);
        } catch (e) {
            console.error('Error evaluating expression for', item.field, e);
            return rawValue;
        }
    }
    return rawValue;
};

// Accent color calculation - moved outside component
const getAccentColor = (call, config, fieldConfig) => {
    // Default to quality score behavior if no accent color config
    if (!config.accentColor) {
        return 'rgb(32, 126, 220)'; // Default blue color
    }

    const accentConfig = config.accentColor;

    // Metric-based coloring
    if (accentConfig.type === 'metric') {
        let value;

        if (accentConfig.metric.expression) {
            try {
                const fn = new Function('record', 'return ' + accentConfig.metric.expression);
                value = fn(call);
            } catch (e) {
                console.error('Error evaluating accent color metric expression:', e);
                value = call[accentConfig.metric.field] || 0;
            }
        } else {
            value = call[accentConfig.metric.field] || 0;
        }

        // Find the field config for this metric
        const metricField = fieldConfig ?
            fieldConfig.find(f => f.id === accentConfig.metric.field) : null;

        // Calculate percentage based on max value
        let maxValue;

        if (accentConfig.metric.useAutoMax && metricField && metricField.maxValue) {
            // Use the pre-calculated max value from field config
            maxValue = metricField.maxValue;
        } else {
            // Use the user-specified max value or default to 100
            maxValue = accentConfig.metric.maxValue || 100;
        }

        const percentage = Math.min(100, Math.max(0, (value / maxValue) * 100));

        // Use the smooth color scale based on percentage
        return getSmoothQualityColor(percentage);
    } else if (accentConfig.type === 'conditions' && Array.isArray(accentConfig.conditions)) {
        // Conditions-based coloring
        for (const condition of accentConfig.conditions) {
            try {
                if (evaluateCondition(condition.condition, call)) {
                    return condition.color;
                }
            } catch (e) {
                console.error('Error evaluating accent color condition:', e);
            }
        }
        return accentConfig.defaultColor || '#e5e7eb';
    } else if (accentConfig.type === 'expression' && accentConfig.expression) {
        // Expression-based coloring
        try {
            const fn = new Function('record', 'return ' + accentConfig.expression);
            return fn(call) || '#e5e7eb';
        } catch (e) {
            console.error('Error evaluating accent color expression:', e);
            return '#e5e7eb';
        }
    }

    return 'rgb(32, 126, 220)'; // Default fallback
};

const CallCard = React.memo(({ call, config, onClick, fieldConfig }) => {

    const { showCallModal } = useModal();

    // Memoize accent color calculation - this is expensive and should only recalculate when needed
    const accentColor = useMemo(() =>
            getAccentColor(call, config, fieldConfig),
        // Only dependencies that would affect accent color
        [call.id, config.accentColor?.type, config.accentColor?.metric?.field]
    );

    // Memoize column values calculation
    const columnValues = useMemo(() => {
        if (!config.columns) return {};
        const values = {};

        config.columns.forEach(column => {
            values[column.id] = calculateFieldValue(column, call);
        });

        return values;
    }, [call, config.columns]);

    // Memoize active flags
    const activeFlags = useMemo(() => {
        if (!config.flags) return [];
        return config.flags.filter(flag => evaluateCondition(flag.condition, call));
    }, [call, config.flags]);

    // Memoize tag values
    const tagValues = useMemo(() => {
        if (!config.tags) return {};
        const values = {};

        config.tags.forEach(tag => {
            values[tag.id] = calculateFieldValue(tag, call);
        });

        return values;
    }, [call, config.tags]);

    const handleCardClick = useCallback(() => {
        showCallModal(call.id);
    }, [call.id, showCallModal]);

    return (
        <div style={{
            position: 'relative',
            padding: '12px 16px 12px 24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'rgb(245, 245, 245)',
            height: '112px',
            boxSizing: 'border-box',
            transition: 'background-color 0.2s ease',
            cursor: 'pointer',
            borderRadius: '8px',
            margin: '8px',
            justifyContent: 'center',
        }}
             onClick={handleCardClick}
             onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
             onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}>
            {/* Accent color bar */}
            <div style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '6px',
                backgroundColor: accentColor,
                borderRadius: '8px 0 0 8px',
            }}/>

            {/* Main content */}
            <div style={{
                display: 'flex',
                gap: '24px',
                flex: '0 0 auto',
                height: '48px',
                alignItems: 'center',
            }}>
                <DateTimeColumn date={call.rawDate} />
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${config.columns?.length || 0}, minmax(120px, 1fr))`,
                    gap: '24px',
                    flex: 1,
                    height: '100%',
                }}>
                    {config.columns?.map((column) => (
                        <Column
                            key={column.id}
                            label={column.label}
                            value={columnValues[column.id]}
                            type={column.type}
                        />
                    ))}
                </div>
            </div>

            {/* Flags and Tags */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                paddingLeft: '144px',
                marginTop: '12px',
                minHeight: '32px',
                flex: '0 0 auto',
            }}>
                {activeFlags.map(flag => (
                    <Flag
                        key={flag.id}
                        label={flag.label}
                        style={{
                            backgroundColor: flag.style.backgroundColor,
                            color: flag.style.color,
                        }}
                    />
                ))}
                {config.tags
                    ?.filter(tag => get(call, tag.field) != null)
                    .map(tag => (
                        <Tag
                            key={tag.id}
                            label={tag.label}
                            value={tagValues[tag.id]}
                            style={tag.style}
                        />
                    ))}
            </div>
        </div>
    );
});

export default CallCard;
